/**
 * The locked main-menu tree (design/chartplotter-menu-structure.md), encoded as
 * typed, testable data so the drill-down menu, search and access map all render
 * from one source. 8 top-level groups, tap-depth scaled to how often a feature is
 * touched, every feature with ONE home (split by intent, never duplicated).
 *
 * `action` names what selecting a leaf does in the real app: a sheet name that
 * Shell already renders, a plotting tool, a store toggle, or 'planned' for things
 * not built yet. Keeping it data means wiring a feature is a one-line change here.
 */

/** How a feature is reached, beyond the menu itself. */
export type AccessChannel = 'menu' | 'tap' | 'ring' | 'strip' | 'hud' | 'auto' | 'voice';

export interface MenuLeaf {
  id: string;
  label: string;
  /** A leading glyph (emoji) for the row, matching the v3 prototype's menu. */
  icon?: string;
  subtitle?: string;
  /** Tap-depth from the spec (0 = every passage … 3 = set-once / buried). */
  depth: number;
  /** Extra channels this is also reachable from (menu is implied). */
  also?: AccessChannel[];
  /** Selection behaviour: a Shell sheet name, a tool id, a toggle, or 'planned'. */
  action?: string;
  /** Not built yet. */
  planned?: boolean;
  /** A radio/toggle group of sub-options. */
  children?: MenuLeaf[];
}

export interface MenuGroup {
  id: string;
  label: string;
  /** A leading glyph (emoji) for the group card, matching the v3 prototype. */
  icon?: string;
  /** A short line under the group title on its card (e.g. "Chart type, overlays, palette"). */
  subtitle?: string;
  /** One of the three groups common underway. */
  frequent?: boolean;
  note?: string;
  items: MenuLeaf[];
}

export interface MenuTree {
  favourites: { id: string; label: string; icon?: string; action?: string }[];
  groups: MenuGroup[];
}

export const MENU_TREE: MenuTree = {
  favourites: [
    { id: 'fav-tidal', label: 'Tidal', icon: '🌊', action: 'tides' },
    { id: 'fav-goto', label: 'Go to', icon: '🔍', action: 'search' },
    { id: 'fav-route', label: 'Route', icon: '🧭', action: 'route' },
    { id: 'fav-anchor', label: 'Anchor', icon: '⚓', action: 'menu:safety/anchor-watch' },
    { id: 'fav-alarms', label: 'Alarms', icon: '📢', action: 'alarms' },
  ],
  groups: [
    {
      id: 'layers',
      label: 'Layers & Chart',
      icon: '🗺️',
      subtitle: 'Chart type, overlays, palette',
      frequent: true,
      note: 'Behind the layers button; frequent but should not cost chart space.',
      items: [
        { id: 'overlays', label: 'Overlays', icon: '🌊', subtitle: 'Satellite, Tidal streams, CATZOC, AIS, Marks, Tracks, Range rings', depth: 1, action: 'control:overlays' },
        { id: 'weather-overlay', label: 'Weather overlay', icon: '🌦️', subtitle: 'wind on the chart, by model, hour by hour', depth: 1, action: 'weather_overlay' },
        { id: 'range-rings', label: 'Range rings', icon: '🎯', subtitle: 'concentric distance rings centred on the boat', depth: 1, action: 'control:rangerings' },
        { id: 'chart-provider', label: 'Chart provider', icon: '🏢', subtitle: 'sets the chart (vector or raster)', depth: 1, action: 'control:provider' },
        { id: 'palette', label: 'Palette', icon: '🌗', subtitle: 'Day / Dusk / Night', depth: 1, action: 'control:palette' },
        { id: 'orientation', label: 'Chart orientation', icon: '🧭', subtitle: 'North / Head / Course up', depth: 1, action: 'control:orientation' },
        { id: 'bearing-ref', label: 'Bearings: True / Magnetic', icon: '🧭', subtitle: 'show bearings in °T or °M + variation (manual or auto/WMM)', depth: 1, action: 'control:bearingdisplay' },
        { id: 'declutter', label: 'Decluttering', icon: '🧹', subtitle: 'chart detail & density', depth: 1, action: 'control:declutter' },
        { id: 'depth-shading', label: 'Depth shading', icon: '🌊', subtitle: 'shallow / deep contour tints', depth: 1, action: 'control:depthshading' },
        { id: 'symbol-set', label: 'Symbol set', icon: '🔣', subtitle: 'NP5011 / NP5012 / Current', depth: 2, action: 'control:symbolset' },
        { id: 'currency', label: 'Chart currency', icon: '📅', subtitle: 'updates & notices', depth: 2, action: 'control:currency' },
      ],
    },
    {
      id: 'search',
      label: 'Search & Go To',
      icon: '🔍',
      subtitle: 'Find place, port, lat-long',
      frequent: true,
      items: [
        { id: 'go-to', label: 'Search / Go to', icon: '🔍', subtitle: 'place, port, lat-long, object', depth: 1, action: 'search', also: ['voice'] },
        { id: 'set-destination', label: 'Set destination', icon: '📍', depth: 1, action: 'set_destination', also: ['ring', 'tap'] },
        { id: 'measure', label: 'Measure tool', icon: '📏', depth: 1, action: 'measure', also: ['ring'] },
        { id: 'ebl-vrm', label: 'EBL / VRM', icon: '➕', subtitle: 'bearing line & range circle from the boat', depth: 1, action: 'eblvrm' },
      ],
    },
    {
      id: 'passage',
      label: 'Passage',
      icon: '🧭',
      // Weather leads the subtitle because that is what people come looking for,
      // and until now there was nothing in the whole menu to find. Conditions was
      // reachable only from Planning mode, from a tide data field, or by voice,
      // so on a boat in Live mode with no tide field set there was no way in at
      // all (Edd, 5 Aug 2026: "I can't see conditions in the menu for getting
      // weather"). A sheet nobody can open is a sheet that does not exist.
      subtitle: 'Weather, tide, routes & planning',
      frequent: true,
      items: [
        { id: 'conditions', label: 'Weather, tide & streams', icon: '🌦️', subtitle: 'wind forecast, height of tide, tidal streams', depth: 1, action: 'conditions' },
        { id: 'route-library', label: 'Routes', icon: '📚', subtitle: 'saved routes & build a new one', depth: 1, action: 'route' },
        { id: 'route-wizard', label: 'Passage plan wizard', icon: '🧭', subtitle: 'plan a passage from scratch, step by step', depth: 1, action: 'passage-wizard' },
        { id: 'planning-tools', label: 'More planning tools', icon: '🧰', subtitle: 'departure advisor, arrival targeting, hazard scan, tidal gates, weather routing', depth: 2, action: 'planning', planned: true },
      ],
    },
    {
      // Group is plain "Safety" (not "Safety & Alarms") so the "Alarms" leaf
      // inside it doesn't read as alarms-nested-in-alarms. "Safety → Alarms" is
      // the clean path to the full alarms sheet.
      id: 'safety',
      label: 'Safety',
      icon: '⚠️',
      subtitle: 'Alarms, anchor watch, distress',
      note: 'Everything that can wake, warn, or demand silence. Display toggles live in Layers.',
      items: [
        { id: 'anchor-watch', label: 'Anchor watch', icon: '⚓', subtitle: 'swing circle, drag, silence', depth: 1, action: 'control:anchorwatch', also: ['ring'] },
        { id: 'alarm-panel', label: 'Alarms', icon: '📢', subtitle: 'all alarms, thresholds, master mute', depth: 1, action: 'alarms', also: ['auto'] },
        { id: 'grounding-alarm', label: 'Grounding & danger cone', icon: '⛰️', subtitle: 'look-ahead danger cone: on/off, distance or time', depth: 1, action: 'control:grounding' },
        { id: 'safety-depth', label: 'Safety depth', icon: '📐', subtitle: 'the depth we treat as our safe limit', depth: 1, action: 'control:safetydepth' },
        { id: 'depth-shading-safety', label: 'Depth shading', icon: '🌊', subtitle: 'chart depth tints (also in Layers)', depth: 1, action: 'control:depthshading' },
        { id: 'bridge-clearance', label: 'Bridge clearance', icon: '🌉', subtitle: 'air draught vs tide', depth: 1, action: 'control:bridgeclearance', also: ['tap'] },
        // MOB recovery view is reached from the on-screen MOB button / HUD, so it
        // doesn't need a redundant menu leaf here (fb-ms394kd8-2).
        // Tide-height only needs to live in the Alarms sheet's "Other" section, so
        // no standalone Safety leaf for it (fb-ms394zh1-3).
        { id: 'distress', label: 'Distress / emergency info', icon: '🆘', subtitle: 'position, nearest havens, Mayday script', depth: 1, action: 'distress', planned: true },
      ],
    },
    {
      id: 'wmt',
      label: 'User data',
      icon: '📌',
      subtitle: 'Your waypoints, marks, tracks & GPX export',
      note: 'Our own saved data: manage and export it here. The show/hide toggles for marks and tracks live in Layers → Overlays.',
      items: [
        { id: 'marks', label: 'Marks & waypoints', icon: '🚩', subtitle: 'go to or delete our saved marks', depth: 1, action: 'control:marks' },
        { id: 'saved-tracks', label: 'Saved tracks', icon: '📚', subtitle: 'save, show/hide, export & delete your tracks', depth: 1, action: 'control:savedtracks' },
        { id: 'track-replay', label: 'Track replay', icon: '⏯️', depth: 2, action: 'control:savedtracks', planned: true },
      ],
    },
    {
      id: 'pilotage',
      label: 'Pilotage & Notes',
      icon: '📐',
      subtitle: 'Notes & plans (geometry on the chart)',
      note: 'Geometry is chart-native (tap). Menu holds notes and plans only.',
      items: [
        { id: 'pilotage-plan', label: 'Pilotage Plan', icon: '📋', subtitle: 'build, save, load & share pilotage plans', depth: 1, action: 'pilotageplans' },
        { id: 'pilotage-community', label: 'Community plans', icon: '🌐', subtitle: 'share & discover plans (preview)', depth: 2, action: 'pilotagecommunity' },
        // The notes & annotations hub: the list of dropped notes (fly to / edit /
        // delete), a New note action, and freehand sketching. Its own in-menu
        // control rather than the plain note sheet, so it's a real home for both.
        { id: 'chart-notes', label: 'Notes & annotations', icon: '✏️', subtitle: 'post-it notes and freehand sketches on the chart', depth: 1, action: 'control:notes' },
      ],
    },
    {
      id: 'boat',
      label: 'Boat, Sensors & Downloads',
      icon: '⛵',
      subtitle: 'Setup & connectivity',
      note: 'Vessel + connectivity + offline merged: users think "boat/system setup".',
      items: [
        { id: 'boat-profile', label: 'Boat profile', icon: '⛵', subtitle: 'draught, air draught, length, cruise speed', depth: 1, action: 'control:boatprofile' },
        { id: 'own-vector', label: 'Own-ship vectors', icon: '➡️', subtitle: 'course & heading vector length (minutes), range rings', depth: 2, action: 'control:ownvector' },
        { id: 'ais-config', label: 'AIS configuration', icon: '🚢', subtitle: 'source, display, vector length', depth: 2, action: 'control:aisconfig' },
        { id: 'polar-file', label: 'Boat polar / VMG', icon: '📈', subtitle: 'sailing polar & target boat speed (VMG)', depth: 2, action: 'control:boatpolar' },
        { id: 'sensors', label: 'GNSS source', icon: '🛰️', subtitle: 'device GNSS or simulated position (NMEA coming soon)', depth: 1, action: 'control:gnsssource' },
        { id: 'multi-profiles', label: 'Multiple profiles', icon: '🗂️', depth: 2, action: 'settings', planned: true },
        { id: 'calibration', label: 'Calibration & offsets', icon: '🎚️', subtitle: 'heading, depth, datum/Z0, wind angle', depth: 2, action: 'settings', planned: true },
        { id: 'offline', label: 'Offline downloads', icon: '⬇️', subtitle: 'charts, tides, weather cached', depth: 1, action: 'settings', planned: true },
      ],
    },
    {
      id: 'app',
      label: 'App & Account',
      icon: '⚙️',
      subtitle: 'Units, account, help',
      note: 'Bury-it-deeper area, but search finds everything.',
      items: [
        // Grouped into a few sensible clusters so App & Account isn't one long flat
        // list (feedback fb-mrxze54l-i / fb-mrxzf5j4-j / fb-mrxzfpca-k).
        {
          id: 'units-format',
          label: 'Units & format',
          icon: '📏',
          subtitle: 'units & position format',
          depth: 2,
          children: [
            { id: 'units', label: 'Units', icon: '📏', subtitle: 'depth, distance & speed units', depth: 2, action: 'control:units' },
            { id: 'position-format', label: 'Position format', icon: '🌐', subtitle: 'deg-min-dec-min, DMS, or decimal', depth: 2, action: 'control:posformat' },
          ],
        },
        {
          // Feedback & sounds folded in here: how the phone itself behaves — screen,
          // background running, buzz and siren — all sit together rather than in a
          // separate group (Edd's feedback fb-ms0umxky-0). Units & format stays on
          // its own, since that's about chart values, not phone behaviour.
          id: 'phone-behaviour',
          label: 'Phone behaviour',
          icon: '📱',
          subtitle: 'screen, background, haptics & sound',
          depth: 2,
          children: [
            { id: 'keep-awake', label: 'Keep screen on', icon: '💡', subtitle: 'stop the phone sleeping during pilotage', depth: 2, action: 'control:keepawake' },
            { id: 'background-gps', label: 'Background GNSS', icon: '🛰️', subtitle: 'keep GNSS, alarms & track alive screen-off', depth: 2, action: 'control:bggps' },
            { id: 'haptics', label: 'Haptics', icon: '📳', subtitle: 'buzz on taps and alarms', depth: 2, action: 'control:haptics' },
            { id: 'alarm-sound', label: 'Alarm sound', icon: '🔊', subtitle: 'per-alarm siren sound and volume', depth: 2, action: 'control:alarmsound' },
          ],
        },
        { id: 'glass', label: 'Glass transparency', icon: '🪟', subtitle: 'how see-through the panels are', depth: 2, action: 'control:glass' },
        { id: 'about', label: 'About', icon: 'ℹ️', subtitle: 'version, updates & reset', depth: 2, action: 'control:about' },
        { id: 'diagnostics', label: 'Diagnostics', icon: '🩺', subtitle: 'how the app last stopped', depth: 2, action: 'control:diagnostics' },
        { id: 'accessibility', label: 'Accessibility', icon: '♿', subtitle: 'text size, contrast, colourblind-safe', depth: 2, action: 'settings', planned: true },
        { id: 'tutorial', label: 'Tutorial & demo', icon: '🎒', depth: 2, action: 'settings', planned: true },
        { id: 'account', label: 'Account & sync', icon: '👤', depth: 2, action: 'settings', planned: true },
        { id: 'help', label: 'Help & support', icon: '❓', depth: 2, action: 'settings', planned: true },
      ],
    },
  ],
};

export interface FlatMenuHit {
  leaf: MenuLeaf;
  /** Breadcrumb of labels from the top group down to (but excluding) the leaf. */
  trail: string[];
}

/** A menu feature together with the top-level group it lives under. */
export interface MenuLocation {
  group: MenuGroup;
  leaf: MenuLeaf;
}

/**
 * Find a feature by its leaf id anywhere in the tree (top items and their
 * nested children), with the group it sits under. Used to launch a favourite or
 * most-used feature from the quick bar exactly as tapping it in the menu would.
 */
export function findMenuLeaf(id: string, tree: MenuTree = MENU_TREE): MenuLocation | undefined {
  for (const group of tree.groups) {
    for (const item of group.items) {
      if (item.id === id) return { group, leaf: item };
      for (const child of item.children ?? []) {
        if (child.id === id) return { group, leaf: child };
      }
    }
  }
  return undefined;
}

/** Flatten the whole tree (groups + nested children) for live search. */
export function flattenMenu(tree: MenuTree = MENU_TREE): FlatMenuHit[] {
  const out: FlatMenuHit[] = [];
  const walk = (leaf: MenuLeaf, trail: string[]) => {
    out.push({ leaf, trail });
    for (const c of leaf.children ?? []) walk(c, [...trail, leaf.label]);
  };
  for (const g of tree.groups) {
    for (const item of g.items) walk(item, [g.label]);
  }
  return out;
}

/** Search the flattened tree by label/subtitle, case-insensitive. */
export function searchMenu(query: string, tree: MenuTree = MENU_TREE): FlatMenuHit[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return flattenMenu(tree).filter(
    (h) =>
      h.leaf.label.toLowerCase().includes(q) ||
      (h.leaf.subtitle ?? '').toLowerCase().includes(q),
  );
}
